import React, { useState } from "react";
import axiosInstance from "../../../interceptors";

// ── Provider catalog ────────────────────────────────────────────────────────
// The shape of each field maps directly to the POST /common/sources/connections/{id}/credentials
// body keys (apiKey, accountId, subdomain). Keep field `name` aligned with those keys.
const PROVIDER_META = {
  NAUKRI: {
    name: "Naukri.com (Resdex)",
    desc: "India's #1 resume database — 7.83 crore resumes",
    authType: "API Key",
    tint: "#FF7B1C",
    fields: [
      { name: "apiKey", label: "Resdex API Key", type: "password", required: true,
        placeholder: "Paste the key from Naukri Partner Portal → API Access",
        hint: "Generated under API Access in your Naukri employer dashboard." },
      { name: "accountId", label: "Naukri Employer ID", type: "text", required: true,
        placeholder: "e.g. 12345", hint: "Numeric ID shown on the Partner Portal home." },
    ],
  },
  FOUNDIT: {
    name: "Foundit (Monster India)",
    desc: "India's #2 resume database — SeekerDB",
    authType: "API Key",
    tint: "#E94235",
    fields: [
      { name: "apiKey", label: "Foundit SeekerDB API Key", type: "password", required: true,
        placeholder: "Bearer token from Foundit Partner Dashboard" },
      { name: "accountId", label: "Foundit Employer ID", type: "text", required: true,
        placeholder: "e.g. FND-12345" },
    ],
  },
  INDEED: {
    name: "Indeed",
    desc: "Job posting via XML feed + Indeed Apply",
    authType: "OAuth 2.0",
    tint: "#2164F3",
    fields: [],
    oauth: true,
  },
  APNA: {
    name: "Apna",
    desc: "Fresher & blue-collar hiring — campus/entry-level",
    authType: "API Key",
    tint: "#0066FF",
    fields: [
      { name: "apiKey", label: "Apna Employer API Key", type: "password", required: true,
        placeholder: "From Apna Partner Dashboard" },
    ],
  },
  AFFINDA: {
    name: "Affinda Parser",
    desc: "AI-powered resume / CV parsing engine",
    authType: "API Key",
    tint: "#7C3AED",
    fields: [
      { name: "apiKey", label: "Affinda API Key", type: "password", required: true,
        placeholder: "From affinda.com → Settings → API Keys",
        hint: "Starts with `aff_`." },
      { name: "subdomain", label: "Collection key (optional)", type: "text", required: false,
        placeholder: "Leave blank to use default collection" },
    ],
  },
  LINKEDIN: {
    name: "LinkedIn",
    desc: "Chrome extension + profile import",
    authType: "Extension",
    tint: "#0A66C2",
    fields: [],
    extension: true,
  },
};

const PROVIDER_ORDER = ["NAUKRI", "LINKEDIN", "FOUNDIT", "INDEED", "APNA", "AFFINDA"];

// ── Logo placeholder ────────────────────────────────────────────────────────
// Until real SVG/PNGs land in /public/logos/, render a tinted box with the
// provider initial. Keeps the grid visually consistent and self-contained.
const ProviderLogo = ({ providerKey, size = 44 }) => {
  const meta = PROVIDER_META[providerKey];
  const tint = meta?.tint || "#94A3B8";
  return (
    <div
      className="rounded-lg flex items-center justify-center font-semibold text-white flex-shrink-0"
      style={{ width: size, height: size, background: `linear-gradient(135deg, ${tint}, ${tint}dd)`, fontSize: size * 0.38 }}
    >
      {providerKey[0]}
    </div>
  );
};

// ── Main modal ──────────────────────────────────────────────────────────────
// Props:
//   onClose()         — close the modal (no connection created)
//   onSuccess(conn)   — a connection row was created + credentialed
//   tenantId          — tenant to create the connection under (default 1)
export default function ConnectSourceModal({ onClose, onSuccess, tenantId = 1 }) {
  const [step, setStep] = useState("picker"); // "picker" | "form"
  const [providerKey, setProviderKey] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const meta = providerKey ? PROVIDER_META[providerKey] : null;

  const pickProvider = (key) => {
    setProviderKey(key);
    setDisplayName(PROVIDER_META[key].name);
    setForm({});
    setError(null);
    setStep("form");
  };

  const backToPicker = () => {
    setStep("picker");
    setProviderKey(null);
    setError(null);
  };

  const updateField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const validate = () => {
    if (!meta) return "Pick a provider first.";
    for (const f of meta.fields) {
      if (f.required && !form[f.name]?.trim?.()) return `${f.label} is required.`;
    }
    return null;
  };

  // Two-step backend pattern: create connection row, then save credentials.
  // For OAuth providers, step 2 is replaced with an auth-url fetch + redirect.
  const handleSubmit = async () => {
    const msg = validate();
    if (msg) { setError(msg); return; }

    setSaving(true);
    setError(null);
    let createdId = null;

    try {
      // 1. Create the connection row
      const createResp = await axiosInstance.post("/common/sources/connections", {
        tenantId,
        provider: providerKey,
        displayName: displayName || meta.name,
      });
      createdId = createResp?.data?.id;
      if (!createdId) throw new Error("Backend did not return a connection id");

      // 2a. OAuth path — fetch the auth URL and redirect the browser
      if (meta.oauth) {
        const authResp = await axiosInstance.get(`/common/sources/connections/${createdId}/auth-url`);
        const url = authResp?.data?.url;
        if (!url) throw new Error("Backend did not return an OAuth authorization URL");
        // Persist minimal context so the post-callback handler on return can match things up
        sessionStorage.setItem("pending_oauth_conn", String(createdId));
        window.location.href = url;
        return;
      }

      // 2b. Chrome extension path — LinkedIn connects via the external extension;
      // the server-issued pairing secret is fetched once the connection row exists.
      if (meta.extension) {
        // No credential POST here — the Chrome extension will pair against this
        // connection id after the admin installs it. For MVP we just mark the row
        // created and close.
        onSuccess?.(createResp.data);
        onClose?.();
        return;
      }

      // 2c. API-key path — POST credentials to the credentials endpoint
      const creds = {};
      for (const f of meta.fields) if (form[f.name]) creds[f.name] = form[f.name].trim();
      const credResp = await axiosInstance.post(
        `/common/sources/connections/${createdId}/credentials`,
        creds
      );

      onSuccess?.(credResp.data || createResp.data);
      onClose?.();
    } catch (e) {
      // axios interceptor normalizes error shapes — honor both {message} and raw Error
      const apiMsg = e?.message || e?.data?.message || e?.data?.error || "Failed to create connection";
      setError(apiMsg);
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Connect a new source"
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-vf-base">
              {step === "picker" ? "Connect a new source" : `Connect ${meta?.name}`}
            </h2>
            <p className="text-xs text-vf-muted mt-0.5">
              {step === "picker"
                ? "Pick a job board, resume database, or parser to wire up."
                : meta?.desc}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-vf-muted hover:text-vf-base text-2xl leading-none px-2"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {step === "picker" && (
            <div className="grid grid-cols-2 gap-3">
              {PROVIDER_ORDER.map((key) => {
                const p = PROVIDER_META[key];
                return (
                  <button
                    key={key}
                    onClick={() => pickProvider(key)}
                    className="text-left border border-slate-200 rounded-lg p-3 hover:border-vf-primary hover:bg-vf-tint transition-colors flex items-center gap-3"
                  >
                    <ProviderLogo providerKey={key} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-vf-base truncate">{p.name}</div>
                      <div className="text-xs text-vf-muted truncate">{p.desc}</div>
                      <div className="text-[10px] mt-1 inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">
                        {p.authType}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {step === "form" && meta && (
            <div>
              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-slate-100">
                <ProviderLogo providerKey={providerKey} size={52} />
                <div>
                  <div className="text-base font-semibold text-vf-base">{meta.name}</div>
                  <div className="text-xs text-vf-muted">{meta.authType}</div>
                </div>
              </div>

              {/* Display name — shown for all providers */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-vf-base mb-1">Label for this connection</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={meta.name}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded focus:outline-none focus:border-vf-primary"
                />
                <div className="text-[11px] text-vf-muted mt-1">Shown in the Source Hub — e.g. "Naukri (Bengaluru team)".</div>
              </div>

              {/* OAuth provider — single button, no fields */}
              {meta.oauth && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-5 text-center">
                  <p className="text-sm text-vf-muted mb-3">
                    You'll be redirected to {meta.name} to authorize Ample Hire to post jobs and receive applications on your behalf.
                  </p>
                  <button
                    onClick={handleSubmit}
                    disabled={saving}
                    className="px-4 py-2 rounded bg-vf-primary text-white text-sm font-medium disabled:opacity-50"
                  >
                    {saving ? "Opening…" : `Continue with ${meta.name} →`}
                  </button>
                </div>
              )}

              {/* Chrome extension provider — install instructions */}
              {meta.extension && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-vf-base">
                  <p className="mb-2">
                    {meta.name} sourcing is handled by the <b>Ample Hire Chrome Extension</b>.
                  </p>
                  <ol className="list-decimal list-inside text-xs text-vf-muted space-y-1 mb-3">
                    <li>Install the extension from the Chrome Web Store.</li>
                    <li>Click this button to register a pairing slot on the server.</li>
                    <li>Open the extension's options page and paste the pairing code shown after save.</li>
                  </ol>
                  <button
                    onClick={handleSubmit}
                    disabled={saving}
                    className="px-4 py-2 rounded bg-vf-primary text-white text-sm font-medium disabled:opacity-50"
                  >
                    {saving ? "Creating…" : "Create pairing slot"}
                  </button>
                </div>
              )}

              {/* API-key provider — explicit fields */}
              {!meta.oauth && !meta.extension && meta.fields.length > 0 && (
                <div className="space-y-4">
                  {meta.fields.map((f) => (
                    <div key={f.name}>
                      <label className="block text-xs font-medium text-vf-base mb-1">
                        {f.label} {f.required && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        type={f.type}
                        value={form[f.name] || ""}
                        onChange={(e) => updateField(f.name, e.target.value)}
                        placeholder={f.placeholder}
                        autoComplete={f.type === "password" ? "new-password" : "off"}
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded focus:outline-none focus:border-vf-primary font-mono"
                      />
                      {f.hint && <div className="text-[11px] text-vf-muted mt-1">{f.hint}</div>}
                    </div>
                  ))}
                </div>
              )}

              {/* Error banner */}
              {error && (
                <div className="mt-4 px-3 py-2 rounded border border-red-200 bg-red-50 text-sm text-red-700">
                  {error}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-3">
          <div>
            {step === "form" && (
              <button
                onClick={backToPicker}
                className="text-xs text-vf-muted hover:text-vf-base"
                disabled={saving}
              >
                ← Pick a different provider
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-3 py-1.5 rounded border border-slate-300 text-xs text-vf-base hover:bg-vf-tint disabled:opacity-50"
            >
              Cancel
            </button>
            {step === "form" && !meta?.oauth && !meta?.extension && (
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="px-4 py-1.5 rounded bg-vf-primary text-white text-xs font-medium disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save connection"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
